import {
  COMPARISON_GRAPH_MAX_FIELDS,
  type ComparisonGraph,
  type ComparisonGraphFieldItem,
  type ComparisonGraphParams,
} from './comparisonGraph';
import { GRAPH_FIELD_TYPE } from './dataKiGraph';

/**
 * グラフ API を何回・どの項目で叩くかを決める。
 * fields → field → デフォルト out の順で resolve する。
 */
export const resolveComparisonGraphFields = (params: {
  field?: string;
  fields?: string[];
}): string[] => {
  if (params.fields && params.fields.length > 0) {
    return params.fields.slice(0, COMPARISON_GRAPH_MAX_FIELDS);
  }
  if (params.field) {
    return [params.field];
  }
  return [GRAPH_FIELD_TYPE.OUT];
};

export const resolveComparisonGraphFieldsForSearch = (
  existingGraphParams: { field?: string; fields?: string[] } | undefined
): string[] => {
  if (existingGraphParams) {
    return resolveComparisonGraphFields(existingGraphParams);
  }
  return [GRAPH_FIELD_TYPE.OUT];
};

export const resolveComparisonGraphFieldsFromFavorite = (setting: {
  field?: string;
  fields?: string[];
}): string[] => resolveComparisonGraphFields(setting);

/**
 * 表示中の graphList、なければ pendingFields から項目一覧を取る。
 * グラフを閉じたあとに再表示するときの記憶元にする。
 */
export const resolveComparisonGraphFieldsFromGraph = (
  graph: Pick<ComparisonGraph, 'graphList' | 'pendingFields'> | undefined
): string[] => {
  if (!graph) {
    return [];
  }
  if (graph.graphList.length > 0) {
    return graph.graphList.map((item) => item.field);
  }
  return graph.pendingFields ?? [];
};

type RememberedComparisonGraphFields = {
  [shuCode: string]: string[] | undefined;
};

/**
 * 閉じる操作で表示データを消す前に、選択中のグラフ項目を残す。
 * 記憶する項目が無いときは引数をそのまま返し、不要な新しい参照を作らない。
 */
export const rememberComparisonGraphFieldsOnHide = (
  rememberedFields: RememberedComparisonGraphFields,
  shuCode: string,
  graph: Pick<ComparisonGraph, 'graphList' | 'pendingFields'> | undefined
): RememberedComparisonGraphFields => {
  const fields = resolveComparisonGraphFieldsFromGraph(graph);
  if (fields.length === 0) {
    return rememberedFields;
  }
  return {
    ...rememberedFields,
    [shuCode]: fields,
  };
};

/**
 * 検索条件セレクタ用。グラフ表示中は graph / pendingFields、閉じていれば記憶した項目を使う。
 */
export const buildComparisonGraphSearchParamsFromGraph = (
  graph: ComparisonGraph | undefined,
  rememberedFields?: readonly string[]
): ComparisonGraphParams => {
  const graphFields = resolveComparisonGraphFieldsFromGraph(graph);
  const fields =
    graphFields.length > 0 ? graphFields : [...(rememberedFields ?? [])];

  if (!graph && fields.length === 0) {
    return {};
  }

  const firstField = fields.at(0) ?? GRAPH_FIELD_TYPE.OUT;
  return {
    ...(graph?.setting ?? {}),
    field: firstField,
    ...(fields.length > 0 ? { fields } : {}),
  };
};

export const buildComparisonGraphFieldsAfterAddEmptySlot = (
  currentFields: readonly string[],
  maxFields: number = COMPARISON_GRAPH_MAX_FIELDS
): string[] => {
  if (currentFields.length >= maxFields) {
    return currentFields.slice(0, maxFields);
  }
  return [...currentFields, ''].slice(0, maxFields);
};

export const buildComparisonGraphFieldsAfterRemove = (
  currentFields: readonly string[],
  index: number,
  maxFields: number = COMPARISON_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= currentFields.length || currentFields.length <= 1) {
    return currentFields.slice(0, maxFields);
  }
  return currentFields
    .filter((_, fieldIndex) => fieldIndex !== index)
    .slice(0, maxFields);
};

export const buildComparisonGraphFieldsAfterIndexChange = (
  currentFields: readonly string[],
  index: number,
  field: string,
  maxFields: number = COMPARISON_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= maxFields) {
    return currentFields.slice(0, maxFields);
  }

  if (field === '') {
    const slotCount = Math.min(
      Math.max(currentFields.length, index + 1),
      maxFields
    );
    return Array.from({ length: slotCount }, (_, slotIndex) =>
      slotIndex === index ? '' : currentFields.at(slotIndex) ?? ''
    );
  }

  const slotCount = Math.min(
    Math.max(currentFields.length, index + 1),
    maxFields
  );

  return Array.from({ length: slotCount }, (_, slotIndex) =>
    slotIndex === index
      ? field
      : currentFields.at(slotIndex) ?? GRAPH_FIELD_TYPE.OUT
  );
};

export const normalizeComparisonGraphFieldsForFetch = (
  fields: readonly string[],
  maxFields: number = COMPARISON_GRAPH_MAX_FIELDS
): string[] => fields.filter((field) => field !== '').slice(0, maxFields);

/**
 * 初回取得、および期間切替など「同じ項目のまま全グラフを再取得」するときに
 * 全体ローダーを出す判定。項目の追加・変更は対象スロットの graphItem が
 * 未取得になりスロット単位ローダーが出るため、全体ローダーは出さない。
 */
export const isComparisonGraphFullReload = (
  isLoading: boolean,
  fieldSlots: readonly string[],
  graphList: readonly Pick<ComparisonGraphFieldItem, 'field'>[]
): boolean => {
  if (!isLoading) {
    return false;
  }
  if (graphList.length === 0) {
    return true;
  }
  return (
    normalizeComparisonGraphFieldsForFetch(fieldSlots).join(',') ===
    graphList.map((item) => item.field).join(',')
  );
};

export const shouldRefetchComparisonGraphFields = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean =>
  normalizeComparisonGraphFieldsForFetch(previousFields).join(',') !==
  normalizeComparisonGraphFieldsForFetch(nextFields).join(',');

const areRemainingFieldsInOrder = (
  remainingFields: readonly string[],
  originalFields: readonly string[]
): boolean =>
  remainingFields.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, field) => {
      if (!state.isMatch) {
        return state;
      }
      const relativeIndex = originalFields
        .slice(state.searchIndex)
        .findIndex((candidate) => candidate === field);
      return relativeIndex === -1
        ? { searchIndex: state.searchIndex, isMatch: false }
        : {
            searchIndex: state.searchIndex + relativeIndex + 1,
            isMatch: true,
          };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

export const isComparisonGraphFieldsRemovalOnly = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrevious =
    normalizeComparisonGraphFieldsForFetch(previousFields);
  const normalizedNext = normalizeComparisonGraphFieldsForFetch(nextFields);
  return (
    normalizedNext.length < normalizedPrevious.length &&
    areRemainingFieldsInOrder(normalizedNext, normalizedPrevious)
  );
};

export const resolveComparisonGraphItemForFieldSlot = (
  graphList: readonly ComparisonGraphFieldItem[],
  fieldSlots: readonly string[],
  slotIndex: number
): ComparisonGraphFieldItem | undefined => {
  const field = fieldSlots.at(slotIndex);
  if (!field) {
    return undefined;
  }
  const graphListIndex = fieldSlots
    .slice(0, slotIndex)
    .filter((slotField) => slotField !== '').length;
  const itemByIndex = graphList.at(graphListIndex);
  return itemByIndex?.field === field
    ? itemByIndex
    : graphList.find((item) => item.field === field);
};

export const buildComparisonGraphListForFields = (
  currentGraphList: readonly ComparisonGraphFieldItem[],
  fields: readonly string[]
): ComparisonGraphFieldItem[] => {
  const graphByField = new Map(
    currentGraphList.map((item) => [item.field, item])
  );
  return fields.flatMap((field) => {
    const item = graphByField.get(field);
    return item ? [item] : [];
  });
};

/**
 * graphList（または取得前の pendingFields）を正としてスロットを同期する。
 * 末尾の未選択スロットだけ残し、お気に入りに無い余分な項目は捨てる。
 */
export const mergeComparisonGraphFieldSlots = (
  previousSlots: readonly string[],
  graphList: readonly Pick<ComparisonGraphFieldItem, 'field'>[],
  options?: {
    maxFields?: number;
    pendingFields?: readonly string[];
  }
): string[] => {
  const maxFields = options?.maxFields ?? COMPARISON_GRAPH_MAX_FIELDS;
  const fieldsFromGraph =
    graphList.length > 0
      ? graphList.map((item) => item.field)
      : (options?.pendingFields ?? []).slice(0, maxFields);

  if (fieldsFromGraph.length === 0) {
    return previousSlots.length > 0 ? previousSlots.slice(0, maxFields) : [];
  }

  const normalizedPrevious = normalizeComparisonGraphFieldsForFetch(
    previousSlots,
    maxFields
  );

  if (normalizedPrevious.join(',') === fieldsFromGraph.join(',')) {
    return previousSlots.slice(0, maxFields);
  }

  const trailingEmptySlots = previousSlots
    .slice(fieldsFromGraph.length)
    .filter((field) => field === '');

  return [...fieldsFromGraph, ...trailingEmptySlots].slice(0, maxFields);
};

export { syncGraphSlotKeys } from './graphSlotKeys';
